import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import Joyride, { CallBackProps, Step, EVENTS, ACTIONS, STATUS as JOYRIDE_STATUS } from 'react-joyride';
import tourSteps from '../../assets/data/tourSteps.json';

interface GlobalTourProps {
  isEnabled?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
}

const GlobalTour = forwardRef<{ restartTour: () => void }, GlobalTourProps>(
  ({ isEnabled = true, onComplete, onSkip }, ref) => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [steps, setSteps] = useState<Step[]>([]);
    const location = useLocation();

    useEffect(() => {
      const currentPath = location.pathname;
      // console.log('Current Path:', currentPath); // Debug: Log the current path

      // Check for exact route match first
      let routeSteps = (tourSteps as { [key: string]: Step[] })[currentPath] || [];

      // If no exact match, check for wildcard patterns
      if (!routeSteps.length) {
        for (const key in tourSteps) {
          if (key.endsWith('/*')) {
            const basePath = key.slice(0, -2); // Remove "/*"
            if (currentPath.startsWith(basePath)) {
              routeSteps = (tourSteps as { [key: string]: Step[] })[key];
              // console.log(`Matched wildcard: ${key}`); // Debug: Log matched wildcard
              break;
            }
          }
        }
      }

      // console.log('Loaded Steps:', routeSteps); // Debug: Log the loaded steps
      setSteps(routeSteps);
    }, [location]);

    // Reset tour when isEnabled or steps change
    useEffect(() => {
      if (isEnabled && steps.length > 0) {
        // console.log('Starting tour with steps:', steps); // Debug: Log when tour starts
        setStepIndex(0);
        setRun(true);
      } else {
        // console.log('Tour not started. isEnabled:', isEnabled, 'steps.length:', steps.length); // Debug: Log why tour didn’t start
        setRun(false);
      }
    }, [isEnabled, steps]);

    useImperativeHandle(ref, () => ({
      restartTour: () => {
        console.log('Restarting tour'); // Debug: Log tour restart
        setStepIndex(0);
        setRun(true);
      },
    }));

    const handleJoyrideCallback = useCallback(
      (data: CallBackProps) => {
        const { action, index, status, type } = data;

        if (status === JOYRIDE_STATUS.FINISHED || status === JOYRIDE_STATUS.SKIPPED) {
          // console.log('Tour ended with status:', status); // Debug: Log tour end
          setRun(false);
          if (status === JOYRIDE_STATUS.FINISHED) {
            onComplete?.();
          } else if (status === JOYRIDE_STATUS.SKIPPED) {
            onSkip?.();
          }
        } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
          // console.log(`Moving to step ${index + (action === ACTIONS.PREV ? -1 : 1)}`); // Debug: Log step change
          setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        }
      },
      [onComplete, onSkip],
    );

    if (!isEnabled || steps.length === 0) {
      // console.log('Tour not rendered. isEnabled:', isEnabled, 'steps.length:', steps.length); // Debug: Log why tour isn’t rendered
      return null;
    }

    return (
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        hideBackButton={false}
        disableOverlayClose
        spotlightClicks
        disableOverlay={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#329619',
            textColor: '#333',
          },
          tooltipContainer: { textAlign: 'left' },
          buttonNext: { backgroundColor: '#329619' },
          buttonBack: { marginRight: 10 },
        }}
        floaterProps={{ disableAnimation: true }}
      />
    );
  },
);

export default GlobalTour;
